// redux/slices/masterBreakSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

const normalizeApiData = (res) => res?.data ?? res;

const handleAuthError = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch { }
  }
};

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

/* ===================== THUNKS ===================== */

export const fetchMasterBreaks = createAsyncThunk(
  "masterBreak/fetchMasterBreaks",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const res = await apiRequest("GET", `/master-break/${uid}`);
      const raw = normalizeApiData(res);

      return Array.isArray(raw) ? raw : [];
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при получении перерывов"));
    }
  }
);

export const addMasterBreak = createAsyncThunk(
  "masterBreak/addMasterBreak",
  async ({ uid, dateOfBreak, startBreak, endBreak, description = null }, { rejectWithValue }) => {
    try {
      if (!uid || !dateOfBreak || !startBreak || !endBreak) {
        throw new Error("Не все данные переданы для добавления перерыва");
      }

      const res = await apiRequest("POST", "/master-break", {
        uid,
        dateOfBreak,
        startBreak,
        endBreak,
        description,
      });

      const data = normalizeApiData(res);

      if (!data) throw new Error("No response data received from server.");

      return {
        ...data,
        uid,
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при добавлении перерыва"));
    }
  }
);

export const deleteMasterBreak = createAsyncThunk(
  "masterBreak/deleteMasterBreak",
  async ({ uid, id }, { rejectWithValue }) => {
    try {
      if (!uid || !id) throw new Error("UID или ID не переданы");

      await apiRequest("DELETE", `/master-break/${uid}/${id}`);

      return { uid, id };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при удалении перерыва"));
    }
  }
);

export const fetchSalonMasterBreaks = createAsyncThunk(
  "masterBreak/fetchSalonMasterBreaks",
  async (uids = [], { rejectWithValue }) => {
    try {
      const rows = await Promise.all(
        (uids || []).map(async (uid) => {
          try {
            const res = await apiRequest("GET", `/master-break/${uid}`);
            const raw = normalizeApiData(res);

            const items = Array.isArray(raw)
              ? raw.map((item) => ({ ...item, uid: String(uid) }))
              : [];

            return {
              uid: String(uid),
              items,
            };
          } catch {
            return {
              uid: String(uid),
              items: [],
            };
          }
        })
      );

      return rows;
    } catch (error) {
      return rejectWithValue(
        error?.message || "Ошибка при салонной загрузке перерывов"
      );
    }
  }
);

export const fetchPublicMasterBreaks = createAsyncThunk(
  "masterBreak/fetchPublicMasterBreaks",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const base = getServerUrl().replace(/\/$/, "");
      const res = await axios.get(`${base}/public/${uid}/master-break`);

      return Array.isArray(res?.data) ? res.data : [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.message ||
        "Ошибка при получении публичных перерывов"
      );
    }
  }
);

export const fetchPublicSalonMasterBreaks = createAsyncThunk(
  "masterBreak/fetchPublicSalonMasterBreaks",
  async (uids = [], { rejectWithValue }) => {
    try {
      const base = getServerUrl().replace(/\/$/, "");

      const rows = await Promise.all(
        (uids || []).map(async (uid) => {
          try {
            const res = await axios.get(`${base}/public/${uid}/master-break`);

            const items = Array.isArray(res?.data)
              ? res.data.map((item) => ({ ...item, uid: String(uid) }))
              : [];

            return {
              uid: String(uid),
              items,
            };
          } catch {
            return {
              uid: String(uid),
              items: [],
            };
          }
        })
      );

      return rows;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.message ||
        "Ошибка при публичной салонной загрузке перерывов"
      );
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  masterBreaks: [],
  status: "idle",
  error: null,

  byUid: {},
  statusByUid: {},
  errorByUid: {},

  addStatus: "idle",
  deleteStatus: "idle",
};

/* ===================== SLICE ===================== */

const masterBreakSlice = createSlice({
  name: "masterBreak",
  initialState,
  reducers: {
    addMasterBreakLocal: (state, action) => {
      const row = action.payload;
      if (!row) return;

      const uid = String(row.uid ?? row._fetchedForUid ?? "");

      state.masterBreaks.push(row);

      if (uid) {
        if (!state.byUid[uid]) state.byUid[uid] = [];

        state.byUid[uid].push({
          ...row,
          uid,
        });

        state.statusByUid[uid] = "succeeded";
        state.errorByUid[uid] = null;
      }
    },

    removeMasterBreakLocal: (state, action) => {
      const { uid, id } = action.payload || {};
      if (!id) return;

      state.masterBreaks = state.masterBreaks.filter(
        (item) => String(item.id) !== String(id)
      );

      if (uid != null) {
        const key = String(uid);
        state.byUid[key] = (state.byUid[key] || []).filter(
          (item) => String(item.id) !== String(id)
        );
      } else {
        Object.keys(state.byUid || {}).forEach((key) => {
          state.byUid[key] = (state.byUid[key] || []).filter(
            (item) => String(item.id) !== String(id)
          );
        });
      }
    },

    clearMasterBreakError: (state) => {
      state.error = null;
      state.errorByUid = {};
    },

    resetMasterBreakStatus: (state) => {
      state.status = "idle";
      state.addStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },

    clearMasterBreaks: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterBreaks.pending, (state, action) => {
        const uid = String(action.meta.arg || "");

        state.status = "loading";
        state.error = null;

        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchMasterBreaks.fulfilled, (state, action) => {
        const uid = String(action.meta.arg || "");

        const arr = Array.isArray(action.payload)
          ? action.payload.map((item) => ({
            ...item,
            uid,
            _fetchedForUid: uid,
          }))
          : [];

        state.status = "succeeded";
        state.masterBreaks = arr;

        if (uid) {
          state.byUid[uid] = arr;
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchMasterBreaks.rejected, (state, action) => {
        const uid = String(action.meta.arg || "");

        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchMasterBreaks failed";

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
          state.byUid[uid] = [];
        }
      })

      .addCase(addMasterBreak.pending, (state) => {
        state.addStatus = "loading";
        state.error = null;
      })
      .addCase(addMasterBreak.fulfilled, (state, action) => {
        state.addStatus = "succeeded";

        const created = action.payload;
        if (!created) return;

        const uid = String(created.uid ?? created._fetchedForUid ?? "");

        state.masterBreaks.push(created);

        if (uid) {
          if (!state.byUid[uid]) state.byUid[uid] = [];

          state.byUid[uid].push({
            ...created,
            uid,
          });

          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(addMasterBreak.rejected, (state, action) => {
        state.addStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "addMasterBreak failed";
      })

      .addCase(deleteMasterBreak.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteMasterBreak.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";

        const { uid, id } = action.payload || {};
        const key = String(uid || "");

        state.masterBreaks = state.masterBreaks.filter(
          (item) => String(item.id) !== String(id)
        );

        if (key) {
          state.byUid[key] = (state.byUid[key] || []).filter(
            (item) => String(item.id) !== String(id)
          );

          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;
        }
      })
      .addCase(deleteMasterBreak.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "deleteMasterBreak failed";
      })

      .addCase(fetchSalonMasterBreaks.pending, (state, action) => {
        const uids = action.meta.arg || [];

        state.status = "loading";
        state.error = null;

        uids.forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "loading";
          state.errorByUid[key] = null;
        });
      })
      .addCase(fetchSalonMasterBreaks.fulfilled, (state, action) => {
        const rows = action.payload || [];

        state.status = "succeeded";
        state.masterBreaks = [];

        rows.forEach(({ uid, items }) => {
          const key = String(uid);

          state.byUid[key] = Array.isArray(items)
            ? items.map((item) => ({ ...item, uid: key }))
            : [];

          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;

          state.masterBreaks.push(...state.byUid[key]);
        });
      })
      .addCase(fetchSalonMasterBreaks.rejected, (state, action) => {
        const uids = action.meta.arg || [];

        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchSalonMasterBreaks failed";

        uids.forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "failed";
          state.errorByUid[key] = state.error;
        });
      })

      .addCase(fetchPublicMasterBreaks.pending, (state, action) => {
        const uid = String(action.meta.arg || "");

        state.status = "loading";
        state.error = null;

        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicMasterBreaks.fulfilled, (state, action) => {
        const uid = String(action.meta.arg || "");

        const arr = Array.isArray(action.payload)
          ? action.payload.map((item) => ({
            ...item,
            uid,
            _fetchedForUid: uid,
          }))
          : [];

        state.status = "succeeded";
        state.masterBreaks = arr;

        if (uid) {
          state.byUid[uid] = arr;
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicMasterBreaks.rejected, (state, action) => {
        const uid = String(action.meta.arg || "");

        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPublicMasterBreaks failed";

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
          state.byUid[uid] = [];
        }
      })

      .addCase(fetchPublicSalonMasterBreaks.pending, (state, action) => {
        const uids = action.meta.arg || [];

        state.status = "loading";
        state.error = null;

        uids.forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "loading";
          state.errorByUid[key] = null;
        });
      })
      .addCase(fetchPublicSalonMasterBreaks.fulfilled, (state, action) => {
        const rows = action.payload || [];

        state.status = "succeeded";
        state.masterBreaks = [];

        rows.forEach(({ uid, items }) => {
          const key = String(uid);

          state.byUid[key] = Array.isArray(items)
            ? items.map((item) => ({ ...item, uid: key }))
            : [];

          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;

          state.masterBreaks.push(...state.byUid[key]);
        });
      })
      .addCase(fetchPublicSalonMasterBreaks.rejected, (state, action) => {
        const uids = action.meta.arg || [];

        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPublicSalonMasterBreaks failed";

        uids.forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "failed";
          state.errorByUid[key] = state.error;
        });
      })

      .addCase(resetStore, () => initialState);
  },
});

export const {
  addMasterBreakLocal,
  removeMasterBreakLocal,
  clearMasterBreakError,
  resetMasterBreakStatus,
  clearMasterBreaks,
} = masterBreakSlice.actions;

export const selectMasterBreaks = (state) =>
  state.masterBreak?.masterBreaks || [];

export const selectMasterBreaksByUid = (state, uid) =>
  state.masterBreak?.byUid?.[String(uid)] || [];

export const selectMasterBreakStatusByUid = (state, uid) =>
  state.masterBreak?.statusByUid?.[String(uid)] || "idle";

export const selectMasterBreakErrorByUid = (state, uid) =>
  state.masterBreak?.errorByUid?.[String(uid)] || null;

export const selectMasterBreaksForSalon = createSelector(
  [(state) => state.masterBreak?.byUid || {}, (_, uids) => uids || []],
  (byUid, uids = []) => {
    if (!uids.length) return [];
    return uids.flatMap((uid) => byUid[String(uid)] || []);
  }
);

export const selectBreaksByDate = createSelector(
  [(state) => state.masterBreak?.byUid || {}, (_, date) => date],
  (byUid, date) => {
    if (!date) return [];

    return Object.keys(byUid).flatMap((uid) =>
      (byUid[uid] || []).filter((row) => row.dateOfBreak === date)
    );
  }
);

export default masterBreakSlice.reducer;